import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Share2, TrendingUp, Sparkles, Calendar, Image as ImageIcon, BarChart3,
  Copy, Trash2, Plus, Loader2, CheckCircle2, AlertTriangle, RefreshCcw,
  Instagram, Facebook, Linkedin, Settings, Upload, X, Edit3, Activity,
  Heart, MessageCircle, Bookmark, Eye, ExternalLink, AlertCircle, Send
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../../utils/api';

const PILARES = ['Bloqueos SII', 'Delitos Tributarios', 'Contabilidad', 'Embargos TGR'];
const TIPOS_POST = ['Alerta', 'Educativo', 'Caso de éxito', 'Recordatorio'];
const ESTADOS = ['Borrador', 'Programado', 'Publicado'];

const PILAR_COLOR = {
  'Bloqueos SII':         { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200',    dot: 'bg-rose-500' },
  'Delitos Tributarios':  { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500' },
  'Contabilidad':         { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500' },
  'Embargos TGR':         { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  dot: 'bg-purple-500' }
};
const ESTADO_COLOR = {
  Borrador:            { bg: 'bg-slate-100',  text: 'text-slate-700',   border: 'border-slate-300' },
  Programado:          { bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200' },
  Publicado:           { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  Error_Publicacion:   { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' }
};

const copyToClipboard = (text, label = 'Copiado') => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} al portapapeles`),
    () => toast.error('No se pudo copiar')
  );
};

const RedesSociales = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState('monitor');
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  // ----- Monitor -----
  const [noticia, setNoticia] = useState({ titulo: '', fuente: '', url: '', contenido: '' });
  const [clasificando, setClasificando] = useState(false);
  const [clasificacion, setClasificacion] = useState(null);

  // ----- Monitor (Noticias automáticas) -----
  const [noticias, setNoticias] = useState([]);
  const [noticiasLoading, setNoticiasLoading] = useState(false);
  const [fetchingNoticias, setFetchingNoticias] = useState(false);
  const [filtroNoticiaEstado, setFiltroNoticiaEstado] = useState('nueva');
  const [filtroNoticiaPilar, setFiltroNoticiaPilar] = useState('all');

  // ----- Generador -----
  const [genForm, setGenForm] = useState({
    tema: '', pilar: PILARES[0], tipo_post: 'Alerta',
    segmento: 'Dueños de empresas y contadores', contexto: ''
  });
  const [generando, setGenerando] = useState(false);
  const [generado, setGenerado] = useState(null);

  // ----- Posts / Calendario -----
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [filtroPilar, setFiltroPilar] = useState('all');
  const [showNewPost, setShowNewPost] = useState(false);
  const [editPost, setEditPost] = useState(null);

  // ----- Biblioteca -----
  const [assets, setAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);

  // ----- Reporte -----
  const [reporte, setReporte] = useState(null);
  const [reporteLoading, setReporteLoading] = useState(false);

  // ----- Stats Sociales (IG+FB orgánico) -----
  const [statsSetup, setStatsSetup] = useState(null);
  const [statsSetupLoading, setStatsSetupLoading] = useState(false);
  const [statsDashboard, setStatsDashboard] = useState(null);
  const [statsTopPosts, setStatsTopPosts] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSyncing, setStatsSyncing] = useState(false);

  // ----- Publicación directa (Fase 3b) -----
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishForm, setPublishForm] = useState({
    caption: '',
    asset_id: '',
    image_url: '',
    plataformas: ['facebook'],
    source: 'generador' // 'generador' | 'manual'
  });
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // ============ Config (carga inicial) ============
  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get('/redes/config');
      setConfig(r.data);
    } catch (e) {
      toast.error('No se pudo cargar configuración');
    }
  }, []);

  const saveConfig = async () => {
    try {
      const r = await api.put('/redes/config', config);
      setConfig(r.data);
      toast.success('Configuración guardada');
      setShowConfig(false);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  // ============ Monitor: noticias automáticas ============
  const loadNoticias = useCallback(async () => {
    setNoticiasLoading(true);
    try {
      const params = { limit: 100 };
      if (filtroNoticiaEstado !== 'all') params.estado = filtroNoticiaEstado;
      if (filtroNoticiaPilar !== 'all') params.pilar = filtroNoticiaPilar;
      const r = await api.get('/redes/noticias', { params });
      setNoticias(r.data.noticias || []);
    } catch (e) {
      toast.error('Error cargando noticias');
    } finally {
      setNoticiasLoading(false);
    }
  }, [filtroNoticiaEstado, filtroNoticiaPilar]);

  const fetchNoticiasAhora = async () => {
    setFetchingNoticias(true);
    try {
      const r = await api.post('/redes/noticias/fetch-ahora');
      if (r.data?.status === 'iniciado') {
        toast.success('Búsqueda iniciada. Las noticias aparecerán en 1-3 minutos. Haz clic en "Recargar".');
      } else {
        toast.success(`${r.data.nuevas || 0} noticias nuevas · ${r.data.duplicadas || 0} duplicadas · ${r.data.antiguas_descartadas || 0} antiguas`);
        loadNoticias();
      }
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setFetchingNoticias(false);
    }
  };

  const updateNoticia = async (id, updates) => {
    try {
      await api.put(`/redes/noticias/${id}`, updates);
      loadNoticias();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  const usarNoticiaEnGenerador = async (n) => {
    setGenForm({
      tema: n.titulo,
      pilar: n.pilar,
      tipo_post: n.tipo_post_sugerido || 'Alerta',
      segmento: n.segmento_afectado || 'Dueños de empresas y contadores',
      contexto: (n.resumen_corto || n.resumen || '') + (n.angulo_recomendado ? `\n\nÁngulo recomendado IA: ${n.angulo_recomendado}` : '')
    });
    await updateNoticia(n.id, { estado: 'usada' });
    setTab('generador');
    toast.info('Datos cargados en el Generador');
  };

  // ============ Monitor: clasificar noticia ============
  const clasificar = async () => {
    if (!noticia.titulo || noticia.titulo.length < 5) {
      toast.error('Ingresa al menos un titular válido');
      return;
    }
    setClasificando(true);
    setClasificacion(null);
    try {
      const r = await api.post('/redes/clasificar-noticia', noticia);
      setClasificacion(r.data);
      toast.success('Noticia clasificada por IA');
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setClasificando(false);
    }
  };

  const usarParaGenerar = () => {
    if (!clasificacion) return;
    setGenForm((g) => ({
      ...g,
      tema: noticia.titulo,
      pilar: clasificacion.pilar || g.pilar,
      tipo_post: clasificacion.tipo_post_sugerido || g.tipo_post,
      segmento: clasificacion.segmento_afectado || g.segmento,
      contexto: (noticia.contenido || '') + '\n\nÁngulo recomendado IA: ' + (clasificacion.angulo_recomendado || '')
    }));
    setTab('generador');
    toast.info('Datos cargados en el Generador');
  };

  // ============ Generador ============
  const generar = async () => {
    if (!genForm.tema || genForm.tema.length < 5) {
      toast.error('Ingresa un tema (mín 5 caracteres)');
      return;
    }
    setGenerando(true);
    setGenerado(null);
    try {
      const r = await api.post('/redes/generar-contenido', genForm);
      setGenerado(r.data);
      toast.success('Contenido generado por IA');
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setGenerando(false);
    }
  };

  const guardarBorrador = async () => {
    if (!generado) return;
    try {
      const ig = generado.instagram || {};
      const fb = generado.facebook || {};
      const li = generado.linkedin || {};
      const igText = [ig.gancho, ig.texto, ig.cta].filter(Boolean).join('\n\n');
      const payload = {
        titulo: genForm.tema.slice(0, 80),
        pilar: genForm.pilar,
        tema: genForm.tema,
        plataformas: ['Instagram', 'Facebook', 'LinkedIn'],
        contenido_ig: igText,
        contenido_fb: [fb.texto, fb.cta].filter(Boolean).join('\n\n'),
        contenido_li: [li.texto, li.cta].filter(Boolean).join('\n\n'),
        hashtags_ig: ig.hashtags || [],
        formato_ig: ig.formato || 'Imagen',
        formato_fb: fb.formato || 'Imagen',
        formato_li: li.formato || 'Imagen'
      };
      await api.post('/redes/posts', payload);
      toast.success('Borrador guardado en el calendario');
      loadPosts();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  // ============ Posts ============
  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const params = {};
      if (filtroEstado !== 'all') params.estado = filtroEstado;
      if (filtroPilar !== 'all') params.pilar = filtroPilar;
      const r = await api.get('/redes/posts', { params });
      setPosts(r.data.posts || []);
    } catch (e) {
      toast.error('Error cargando posts');
    } finally {
      setPostsLoading(false);
    }
  }, [filtroEstado, filtroPilar]);

  const updatePost = async (id, updates) => {
    try {
      await api.put(`/redes/posts/${id}`, updates);
      toast.success('Post actualizado');
      loadPosts();
      setEditPost(null);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('¿Eliminar este post?')) return;
    try {
      await api.delete(`/redes/posts/${id}`);
      toast.success('Post eliminado');
      loadPosts();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  // ============ Biblioteca ============
  const loadAssets = useCallback(async () => {
    try {
      const r = await api.get('/redes/biblioteca');
      setAssets(r.data.assets || []);
    } catch (e) {
      toast.error('Error cargando biblioteca');
    }
  }, []);

  const uploadAsset = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAsset(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/redes/biblioteca', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Archivo subido');
      loadAssets();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setUploadingAsset(false);
      e.target.value = '';
    }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm('¿Eliminar este asset?')) return;
    try {
      await api.delete(`/redes/biblioteca/${id}`);
      toast.success('Asset eliminado');
      loadAssets();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  // ============ Reporte ============
  const loadReporte = useCallback(async () => {
    setReporteLoading(true);
    try {
      const r = await api.get('/redes/reporte-semanal');
      setReporte(r.data);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setReporteLoading(false);
    }
  }, []);

  // ============ Stats Sociales (IG+FB) ============
  const loadStatsSetup = useCallback(async () => {
    setStatsSetupLoading(true);
    try {
      const r = await api.get('/redes/stats/setup-status');
      setStatsSetup(r.data);
    } catch (e) {
      toast.error('Error consultando estado Meta');
    } finally {
      setStatsSetupLoading(false);
    }
  }, []);

  const loadStatsDashboard = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [d, p] = await Promise.all([
        api.get('/redes/stats/dashboard?dias=30'),
        api.get('/redes/stats/posts?sort_by=engagement&limit=10')
      ]);
      setStatsDashboard(d.data);
      setStatsTopPosts(p.data.posts || []);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const syncStats = async () => {
    setStatsSyncing(true);
    try {
      await api.post('/redes/stats/sync');
      toast.success('Sincronización iniciada. Recarga en 30-60s.');
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setStatsSyncing(false);
    }
  };

  // ============ Publicación directa ============
  const abrirPublicarDesdeGenerador = (plataforma) => {
    if (!generado) return;
    const data = generado[plataforma === 'facebook' ? 'facebook' : 'instagram'] || {};
    const caption = [data.gancho, data.texto, data.cta, Array.isArray(data.hashtags) ? data.hashtags.join(' ') : '']
      .filter(Boolean).join('\n\n');
    setPublishForm({
      caption,
      asset_id: '',
      image_url: '',
      plataformas: [plataforma],
      source: 'generador'
    });
    setPublishResult(null);
    setShowPublishModal(true);
  };

  const abrirPublicarManual = () => {
    setPublishForm({
      caption: '',
      asset_id: '',
      image_url: '',
      plataformas: ['facebook'],
      source: 'manual'
    });
    setPublishResult(null);
    setShowPublishModal(true);
  };

  const publicar = async () => {
    if (!publishForm.caption || publishForm.caption.length < 5) {
      toast.error('La publicación debe tener al menos 5 caracteres');
      return;
    }
    if (publishForm.plataformas.includes('instagram') && !publishForm.asset_id && !publishForm.image_url) {
      toast.error('Instagram requiere una imagen (sube un asset o pega una URL pública)');
      return;
    }
    setPublishing(true);
    setPublishResult(null);
    try {
      const r = await api.post('/redes/publicar', publishForm);
      setPublishResult(r.data);
      if (r.data.ok) {
        toast.success('¡Publicado correctamente en Meta!');
      } else {
        toast.error('Algunas plataformas fallaron — ver detalles abajo');
      }
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setPublishing(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (tab === 'monitor') loadNoticias();
    if (tab === 'calendario') loadPosts();
    if (tab === 'biblioteca') loadAssets();
    if (tab === 'reporte' && !reporte) loadReporte();
    if (tab === 'stats') {
      loadStatsSetup();
      loadStatsDashboard();
    }
  }, [tab, loadNoticias, loadPosts, loadAssets, loadReporte, loadStatsSetup, loadStatsDashboard, reporte]);

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="redes-sociales-page">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Share2 className="w-10 h-10 text-blue-500" strokeWidth={1.5} />
              <div>
                <h1 className="text-4xl font-bold text-slate-900">Redes Sociales</h1>
                <p className="text-base text-slate-600 mt-1">
                  Director Estratégico IA · Monitor de tendencias · Generador multi-plataforma
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowConfig(true)}
              data-testid="btn-config-redes"
              className="gap-2"
            >
              <Settings className="w-4 h-4" /> Configuración
            </Button>
            <Button
              onClick={abrirPublicarManual}
              data-testid="btn-publicar-rapido"
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4" /> Publicar rápido
            </Button>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList className="grid grid-cols-6 max-w-4xl">
              <TabsTrigger value="monitor" data-testid="tab-monitor">
                <TrendingUp className="w-4 h-4 mr-2" /> Monitor
              </TabsTrigger>
              <TabsTrigger value="generador" data-testid="tab-generador">
                <Sparkles className="w-4 h-4 mr-2" /> Generador
              </TabsTrigger>
              <TabsTrigger value="calendario" data-testid="tab-calendario">
                <Calendar className="w-4 h-4 mr-2" /> Calendario
              </TabsTrigger>
              <TabsTrigger value="biblioteca" data-testid="tab-biblioteca">
                <ImageIcon className="w-4 h-4 mr-2" /> Biblioteca
              </TabsTrigger>
              <TabsTrigger value="reporte" data-testid="tab-reporte">
                <BarChart3 className="w-4 h-4 mr-2" /> Reporte
              </TabsTrigger>
              <TabsTrigger value="stats" data-testid="tab-stats">
                <Activity className="w-4 h-4 mr-2" /> Stats IG+FB
              </TabsTrigger>
            </TabsList>

            {/* ============ MONITOR ============ */}
            <TabsContent value="monitor" className="mt-6">
              <Card className="p-4 mb-4 flex flex-wrap items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">Monitor Automático de Tendencias</p>
                    <p className="text-xs text-slate-600">
                      Trae noticias tributarias chilenas de los últimos 60 días desde Google News y las clasifica con IA. Fetch automático cada día a las 08:00 (Chile).
                    </p>
                  </div>
                </div>
                <Button
                  onClick={fetchNoticiasAhora}
                  disabled={fetchingNoticias}
                  data-testid="btn-fetch-ahora"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {fetchingNoticias ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                  Buscar ahora
                </Button>
              </Card>

              <Card className="p-3 mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-600">Filtrar:</span>
                <Select value={filtroNoticiaEstado} onValueChange={setFiltroNoticiaEstado}>
                  <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="nueva">Nuevas</SelectItem>
                    <SelectItem value="vista">Vistas</SelectItem>
                    <SelectItem value="usada">Usadas</SelectItem>
                    <SelectItem value="descartada">Descartadas</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroNoticiaPilar} onValueChange={setFiltroNoticiaPilar}>
                  <SelectTrigger className="w-56 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los pilares</SelectItem>
                    {PILARES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={loadNoticias}>
                  <RefreshCcw className="w-4 h-4 mr-2" /> Recargar
                </Button>
                <span className="text-sm text-slate-500 ml-auto">{noticias.length} noticias</span>
              </Card>

              {noticiasLoading && (
                <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
              )}
              {!noticiasLoading && noticias.length === 0 && (
                <Card className="p-12 text-center text-slate-400" data-testid="noticias-empty">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No hay noticias {filtroNoticiaEstado !== 'all' ? filtroNoticiaEstado + 's' : ''} todavía.</p>
                  <p className="text-sm mt-1">Haz clic en "Buscar ahora" para traer las últimas noticias tributarias.</p>
                </Card>
              )}
              {!noticiasLoading && noticias.length > 0 && (
                <div className="space-y-3" data-testid="noticias-list">
                  {noticias.map((n) => (
                    <NoticiaCard
                      key={n.id}
                      noticia={n}
                      onUsar={() => usarNoticiaEnGenerador(n)}
                      onDescartar={() => updateNoticia(n.id, { estado: 'descartada' })}
                      onMarcarVista={() => updateNoticia(n.id, { estado: 'vista' })}
                    />
                  ))}
                </div>
              )}

              {/* Clasificador manual (legacy / pegar noticia propia) */}
              <details className="mt-8">
                <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-900 mb-3">
                  ¿Quieres clasificar una noticia que tengas tú mismo? Abre el clasificador manual ↓
                </summary>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-3">
                  <Card className="p-6">
                    <h3 className="text-base font-semibold text-slate-900 mb-3">Clasificador Manual</h3>
                    <div className="space-y-3">
                      <div>
                        <Label>Titular / Noticia *</Label>
                        <Input
                          data-testid="input-titular"
                          value={noticia.titulo}
                          onChange={(e) => setNoticia({ ...noticia, titulo: e.target.value })}
                          placeholder="Ej: SII anuncia nuevas fiscalizaciones..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Fuente</Label>
                          <Input
                            value={noticia.fuente}
                            onChange={(e) => setNoticia({ ...noticia, fuente: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>URL</Label>
                          <Input
                            value={noticia.url}
                            onChange={(e) => setNoticia({ ...noticia, url: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Contenido</Label>
                        <Textarea
                          rows={3}
                          value={noticia.contenido}
                          onChange={(e) => setNoticia({ ...noticia, contenido: e.target.value })}
                        />
                      </div>
                      <Button
                        onClick={clasificar}
                        disabled={clasificando}
                        data-testid="btn-clasificar"
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {clasificando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Clasificar con IA
                      </Button>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h3 className="text-base font-semibold text-slate-900 mb-3">Resultado</h3>
                    {!clasificacion && <p className="text-sm text-slate-400">Aún sin clasificar.</p>}
                    {clasificacion && (
                      <div className="space-y-3" data-testid="clasificacion-result">
                        <Badge className={`${PILAR_COLOR[clasificacion.pilar]?.bg} ${PILAR_COLOR[clasificacion.pilar]?.text} ${PILAR_COLOR[clasificacion.pilar]?.border} border`}>
                          {clasificacion.pilar}
                        </Badge>
                        <p className="text-sm"><b>Tipo:</b> {clasificacion.tipo_post_sugerido}</p>
                        <p className="text-sm"><b>Urgencia:</b> {clasificacion.urgencia}</p>
                        <p className="text-sm"><b>Segmento:</b> {clasificacion.segmento_afectado}</p>
                        <p className="text-sm"><b>Resumen:</b> {clasificacion.resumen_corto}</p>
                        <p className="text-sm"><b>Ángulo:</b> {clasificacion.angulo_recomendado}</p>
                        <Button
                          onClick={usarParaGenerar}
                          data-testid="btn-usar-en-generador"
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Sparkles className="w-4 h-4 mr-2" /> Usar en Generador
                        </Button>
                      </div>
                    )}
                  </Card>
                </div>
              </details>
            </TabsContent>

            {/* ============ GENERADOR ============ */}
            <TabsContent value="generador" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-6 lg:col-span-1">
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">Briefing del Post</h2>
                  <p className="text-sm text-slate-600 mb-4">
                    La IA generará 3 versiones (IG/FB/LinkedIn) con tono Peritum y CTA al WhatsApp.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label>Tema *</Label>
                      <Textarea
                        rows={3}
                        data-testid="input-tema"
                        value={genForm.tema}
                        onChange={(e) => setGenForm({ ...genForm, tema: e.target.value })}
                        placeholder="Ej: Nuevas fiscalizaciones SII a PYMEs por boletas electrónicas..."
                      />
                    </div>
                    <div>
                      <Label>Pilar</Label>
                      <Select value={genForm.pilar} onValueChange={(v) => setGenForm({ ...genForm, pilar: v })}>
                        <SelectTrigger data-testid="select-pilar"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PILARES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de post</Label>
                      <Select value={genForm.tipo_post} onValueChange={(v) => setGenForm({ ...genForm, tipo_post: v })}>
                        <SelectTrigger data-testid="select-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIPOS_POST.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Segmento</Label>
                      <Input
                        value={genForm.segmento}
                        onChange={(e) => setGenForm({ ...genForm, segmento: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Contexto / Noticia base</Label>
                      <Textarea
                        rows={3}
                        value={genForm.contexto}
                        onChange={(e) => setGenForm({ ...genForm, contexto: e.target.value })}
                        placeholder="Datos adicionales que la IA debe considerar..."
                      />
                    </div>
                    <Button
                      onClick={generar}
                      disabled={generando}
                      data-testid="btn-generar"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Generar con IA
                    </Button>
                  </div>
                </Card>

                <div className="lg:col-span-2 space-y-4">
                  {!generado && (
                    <Card className="p-12 text-center text-slate-400">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p>Configura el briefing y haz clic en "Generar con IA" para ver las 3 versiones.</p>
                    </Card>
                  )}
                  {generado && (
                    <>
                      <PlataformaCard
                        icon={Instagram}
                        title="Instagram"
                        color="rose"
                        data={generado.instagram}
                        config={config}
                        kind="ig"
                        onPublicar={() => abrirPublicarDesdeGenerador('instagram')}
                        data-testid="card-instagram"
                      />
                      <PlataformaCard
                        icon={Facebook}
                        title="Facebook"
                        color="blue"
                        data={generado.facebook}
                        config={config}
                        kind="fb"
                        onPublicar={() => abrirPublicarDesdeGenerador('facebook')}
                        data-testid="card-facebook"
                      />
                      <PlataformaCard
                        icon={Linkedin}
                        title="LinkedIn"
                        color="indigo"
                        data={generado.linkedin}
                        config={config}
                        kind="li"
                        data-testid="card-linkedin"
                      />
                      {generado._warning && (
                        <Card className="p-4 bg-amber-50 border-amber-200 text-amber-800 text-sm flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> {generado._warning}
                        </Card>
                      )}
                      <Button
                        onClick={guardarBorrador}
                        data-testid="btn-guardar-borrador"
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Guardar como Borrador en Calendario
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ============ CALENDARIO ============ */}
            <TabsContent value="calendario" className="mt-6">
              <Card className="p-4 mb-4 bg-emerald-50/40 border-emerald-200 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <p className="font-semibold text-emerald-900">Publicación automática activa</p>
                  <p className="mt-0.5">
                    Los posts en estado <Badge variant="outline" className="bg-indigo-50 text-indigo-700 text-xs mx-1">Programado</Badge>
                    con fecha futura se publicarán automáticamente en Facebook e Instagram cada 5 minutos.
                    Necesitas asignar <code className="text-xs bg-white px-1 rounded border">contenido_ig</code> y/o
                    <code className="text-xs bg-white px-1 rounded border ml-1">contenido_fb</code> para que aparezcan en sus plataformas.
                    Para IG, recuerda asignar también un asset de la biblioteca.
                  </p>
                </div>
              </Card>

              <Card className="p-4 mb-4 flex flex-wrap items-center gap-3">
                <div className="text-sm text-slate-600">Filtrar:</div>
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                  <SelectTrigger className="w-44"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filtroPilar} onValueChange={setFiltroPilar}>
                  <SelectTrigger className="w-56"><SelectValue placeholder="Pilar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los pilares</SelectItem>
                    {PILARES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={loadPosts}>
                  <RefreshCcw className="w-4 h-4 mr-2" /> Recargar
                </Button>
                <div className="text-sm text-slate-500 ml-auto">{posts.length} posts</div>
              </Card>

              {postsLoading && (
                <div className="text-center py-12 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </div>
              )}
              {!postsLoading && posts.length === 0 && (
                <Card className="p-12 text-center text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No hay posts en el calendario.</p>
                  <p className="text-sm mt-1">Crea uno desde el Generador o haz clic en "Nuevo Post" abajo.</p>
                  <Button className="mt-4" onClick={() => setShowNewPost(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Post
                  </Button>
                </Card>
              )}
              {!postsLoading && posts.length > 0 && (
                <div className="space-y-3" data-testid="posts-list">
                  {posts.map((p) => (
                    <PostRow
                      key={p.id}
                      post={p}
                      onEdit={() => setEditPost(p)}
                      onDelete={() => deletePost(p.id)}
                      onUpdate={(upd) => updatePost(p.id, upd)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ============ BIBLIOTECA ============ */}
            <TabsContent value="biblioteca" className="mt-6">
              <Card className="p-6 mb-4 border-dashed border-2">
                <Label className="font-semibold">Subir imagen / video (máx 20 MB)</Label>
                <div className="flex items-center gap-3 mt-3">
                  <Input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.mp4,.mov"
                    onChange={uploadAsset}
                    disabled={uploadingAsset}
                    data-testid="input-upload-asset"
                  />
                  {uploadingAsset && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
                </div>
              </Card>

              {assets.length === 0 && (
                <Card className="p-12 text-center text-slate-400">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p>No hay archivos en la biblioteca.</p>
                </Card>
              )}
              {assets.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="assets-grid">
                  {assets.map((a) => (
                    <AssetCard key={a.id} asset={a} onDelete={() => deleteAsset(a.id)} />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ============ REPORTE ============ */}
            <TabsContent value="reporte" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Reporte Semanal de Aprendizaje</h2>
                <Button variant="outline" size="sm" onClick={loadReporte} disabled={reporteLoading}>
                  {reporteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                  Recargar
                </Button>
              </div>
              {reporteLoading && (
                <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
              )}
              {!reporteLoading && reporte?.vacio && (
                <Card className="p-12 text-center text-slate-500" data-testid="reporte-vacio">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">{reporte.mensaje}</p>
                  <p className="text-sm mt-2 text-slate-400">{reporte.sugerencia}</p>
                </Card>
              )}
              {!reporteLoading && reporte && !reporte.vacio && (
                <ReporteView reporte={reporte} />
              )}
            </TabsContent>

            {/* ============ STATS IG+FB ============ */}
            <TabsContent value="stats" className="mt-6">
              <StatsSocialesView
                setup={statsSetup}
                setupLoading={statsSetupLoading}
                dashboard={statsDashboard}
                topPosts={statsTopPosts}
                loading={statsLoading}
                syncing={statsSyncing}
                onSync={syncStats}
                onRefresh={() => { loadStatsSetup(); loadStatsDashboard(); }}
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* ============ MODAL CONFIG ============ */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configuración Redes Sociales</DialogTitle>
            <DialogDescription>Estos datos se usan en los CTAs generados por la IA.</DialogDescription>
          </DialogHeader>
          {config && (
            <div className="space-y-3 mt-2">
              <div>
                <Label>WhatsApp</Label>
                <Input
                  data-testid="input-whatsapp"
                  value={config.whatsapp || ''}
                  onChange={(e) => setConfig({ ...config, whatsapp: e.target.value })}
                  placeholder="+56981984930"
                />
              </div>
              <div>
                <Label>Web</Label>
                <Input
                  value={config.web || ''}
                  onChange={(e) => setConfig({ ...config, web: e.target.value })}
                />
              </div>
              <div>
                <Label>Frase CTA base</Label>
                <Textarea
                  rows={2}
                  value={config.cta_frase || ''}
                  onChange={(e) => setConfig({ ...config, cta_frase: e.target.value })}
                />
              </div>
              <div>
                <Label>Tono de marca</Label>
                <Textarea
                  rows={2}
                  value={config.tono_marca || ''}
                  onChange={(e) => setConfig({ ...config, tono_marca: e.target.value })}
                />
              </div>
              <div>
                <Label>Firma</Label>
                <Input
                  value={config.firma_marca || ''}
                  onChange={(e) => setConfig({ ...config, firma_marca: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={saveConfig} data-testid="btn-guardar-config" className="bg-blue-600 hover:bg-blue-700">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL EDIT POST ============ */}
      <EditPostModal
        post={editPost}
        onClose={() => setEditPost(null)}
        onSave={(upd) => updatePost(editPost.id, upd)}
      />

      {/* ============ MODAL NEW POST ============ */}
      <NewPostModal
        open={showNewPost}
        onClose={() => setShowNewPost(false)}
        onCreated={() => { setShowNewPost(false); loadPosts(); }}
      />

      {/* ============ MODAL PUBLICAR DIRECTO ============ */}
      <PublicarModal
        open={showPublishModal}
        form={publishForm}
        setForm={setPublishForm}
        publishing={publishing}
        result={publishResult}
        onClose={() => { setShowPublishModal(false); setPublishResult(null); }}
        onPublicar={publicar}
        assets={assets}
        onLoadAssets={loadAssets}
      />
    </div>
  );
};

// ============ Subcomponentes ============

const URGENCIA_COLOR = {
  alta:  { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  media: { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  baja:  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' }
};

const NoticiaCard = ({ noticia: n, onUsar, onDescartar, onMarcarVista }) => {
  const pc = PILAR_COLOR[n.pilar] || PILAR_COLOR['Contabilidad'];
  const uc = URGENCIA_COLOR[n.urgencia] || URGENCIA_COLOR.media;
  const isNew = n.estado === 'nueva';
  const isUsada = n.estado === 'usada';
  const isDesc = n.estado === 'descartada';

  const fechaPub = n.published_at ? new Date(n.published_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  return (
    <Card
      className={`p-4 transition ${isNew ? 'border-l-4 border-l-blue-500' : ''} ${isDesc ? 'opacity-50' : ''} ${isUsada ? 'bg-emerald-50/30' : ''}`}
      data-testid={`noticia-${n.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className={`${pc.bg} ${pc.text} ${pc.border} border`}>{n.pilar}</Badge>
            <Badge className={`${uc.bg} ${uc.text} ${uc.border} border capitalize text-xs`}>
              Urgencia {n.urgencia}
            </Badge>
            {n.tipo_post_sugerido && (
              <Badge variant="outline" className="text-xs">{n.tipo_post_sugerido}</Badge>
            )}
            {n.relevancia_peritum === 'alta' && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 border text-xs">⭐ Relevancia alta</Badge>
            )}
            {isUsada && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Usada</Badge>}
            {isDesc && <Badge className="bg-slate-100 text-slate-600 text-xs">Descartada</Badge>}
          </div>
          <h3 className="font-semibold text-slate-900">{n.titulo}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {n.fuente}{fechaPub ? ` · ${fechaPub}` : ''}
          </p>
          {n.resumen_corto && (
            <p className="text-sm text-slate-700 mt-2">{n.resumen_corto}</p>
          )}
          {n.angulo_recomendado && (
            <p className="text-sm text-blue-700 mt-1.5 italic">
              <Sparkles className="w-3.5 h-3.5 inline mr-1" />
              {n.angulo_recomendado}
            </p>
          )}
          {n.link && (
            <a
              href={n.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => isNew && onMarcarVista && onMarcarVista()}
              className="text-xs text-blue-600 hover:underline mt-2 inline-block"
            >
              Leer noticia completa ↗
            </a>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {!isUsada && !isDesc && (
            <Button
              size="sm"
              onClick={onUsar}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
              data-testid={`btn-usar-${n.id}`}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Generar post
            </Button>
          )}
          {!isDesc && (
            <Button
              size="sm"
              variant="outline"
              onClick={onDescartar}
              className="text-slate-500 w-full"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Descartar
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

const PlataformaCard = ({ icon: Icon, title, color, data, config, onPublicar }) => {
  if (!data) return null;
  const colors = {
    rose:   { bg: 'bg-rose-50',   border: 'border-rose-200',   icon: 'text-rose-600',   ring: 'ring-rose-500' },
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'text-blue-600',   ring: 'ring-blue-500' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', ring: 'ring-indigo-500' }
  }[color];

  const allText = [
    data.gancho,
    data.texto,
    data.cta,
    Array.isArray(data.hashtags) && data.hashtags.length ? data.hashtags.join(' ') : null,
    config?.whatsapp ? `WhatsApp: ${config.whatsapp}` : null
  ].filter(Boolean).join('\n\n');

  return (
    <Card className={`p-5 border ${colors.border} ${colors.bg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${colors.icon}`} />
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {data.formato && <Badge variant="outline" className="bg-white text-xs">{data.formato}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => copyToClipboard(allText, `Post ${title}`)} className="gap-2">
            <Copy className="w-3.5 h-3.5" /> Copiar
          </Button>
          {onPublicar && (
            <Button
              size="sm"
              onClick={onPublicar}
              className={`${color === 'rose' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'} gap-2`}
              data-testid={`btn-publicar-${title.toLowerCase()}`}
            >
              <Send className="w-3.5 h-3.5" /> Publicar
            </Button>
          )}
        </div>
      </div>
      {data.gancho && (
        <div className="mb-2">
          <Label className="text-xs uppercase text-slate-500">Gancho</Label>
          <p className="text-base font-semibold text-slate-800 mt-0.5">{data.gancho}</p>
        </div>
      )}
      {data.texto && (
        <div className="mb-2">
          <Label className="text-xs uppercase text-slate-500">Texto</Label>
          <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{data.texto}</p>
        </div>
      )}
      {data.cta && (
        <div className="mb-2">
          <Label className="text-xs uppercase text-slate-500">CTA</Label>
          <p className="text-sm text-emerald-700 font-medium mt-0.5">{data.cta}</p>
        </div>
      )}
      {Array.isArray(data.hashtags) && data.hashtags.length > 0 && (
        <div className="mt-3">
          <Label className="text-xs uppercase text-slate-500">Hashtags</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {data.hashtags.map((h, i) => (
              <Badge key={i} variant="outline" className="bg-white text-xs font-normal">{h}</Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const PostRow = ({ post, onEdit, onDelete, onUpdate }) => {
  const pc = PILAR_COLOR[post.pilar] || PILAR_COLOR['Contabilidad'];
  const ec = ESTADO_COLOR[post.estado] || ESTADO_COLOR['Borrador'];
  const esError = post.estado === 'Error_Publicacion';
  return (
    <Card className={`p-4 hover:shadow-md transition ${esError ? 'border-rose-300' : ''}`} data-testid={`post-row-${post.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={`${pc.bg} ${pc.text} ${pc.border} border`}>{post.pilar}</Badge>
            <Badge className={`${ec.bg} ${ec.text} ${ec.border} border`}>
              {post.estado === 'Error_Publicacion' ? 'Error de publicación' : post.estado}
            </Badge>
            {post.publicacion_automatica && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 border text-xs">
                <Sparkles className="w-3 h-3 mr-1 inline" /> Auto
              </Badge>
            )}
            {(post.plataformas || []).map((pl) => (
              <Badge key={pl} variant="outline" className="text-xs">{pl}</Badge>
            ))}
          </div>
          <h3 className="font-semibold text-slate-900 truncate">{post.titulo}</h3>
          {post.fecha_programada && (
            <p className="text-xs text-slate-500 mt-1">
              {post.estado === 'Programado' ? 'Programado para: ' : 'Programación: '}
              {new Date(post.fecha_programada).toLocaleString('es-CL')}
            </p>
          )}
          {post.fecha_publicado && (
            <p className="text-xs text-emerald-700 mt-1">
              Publicado: {new Date(post.fecha_publicado).toLocaleString('es-CL')}
              {post.resultados_meta?.facebook?.permalink_url && (
                <a href={post.resultados_meta.facebook.permalink_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline ml-2">FB ↗</a>
              )}
              {post.resultados_meta?.instagram?.permalink_url && (
                <a href={post.resultados_meta.instagram.permalink_url} target="_blank" rel="noreferrer" className="text-rose-600 hover:underline ml-2">IG ↗</a>
              )}
            </p>
          )}
          {esError && post.error_publicacion && (
            <p className="text-xs text-rose-700 mt-1 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{post.error_publicacion}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {post.estado !== 'Publicado' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdate({ estado: 'Publicado' })}
              className="text-emerald-700 border-emerald-200"
              data-testid={`btn-publicar-${post.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marcar publicado
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit3 className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="text-rose-600">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

const EditPostModal = ({ post, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(post ? { ...post } : null); }, [post]);
  if (!form) return null;
  const eng = form.engagement || {};
  const setEng = (plat, key, val) => {
    setForm({
      ...form,
      engagement: {
        ...eng,
        [plat]: { ...(eng[plat] || {}), [key]: Number(val) || 0 }
      }
    });
  };
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Título</Label>
            <Input value={form.titulo || ''} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pilar</Label>
              <Select value={form.pilar} onValueChange={(v) => setForm({ ...form, pilar: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PILARES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Fecha programada (ISO)</Label>
            <Input
              type="datetime-local"
              value={form.fecha_programada ? form.fecha_programada.slice(0, 16) : ''}
              onChange={(e) => setForm({ ...form, fecha_programada: e.target.value ? new Date(e.target.value).toISOString() : null })}
            />
          </div>
          <div>
            <Label>Instagram</Label>
            <Textarea rows={5} value={form.contenido_ig || ''} onChange={(e) => setForm({ ...form, contenido_ig: e.target.value })} />
          </div>
          <div>
            <Label>Facebook</Label>
            <Textarea rows={5} value={form.contenido_fb || ''} onChange={(e) => setForm({ ...form, contenido_fb: e.target.value })} />
          </div>
          <div>
            <Label>LinkedIn</Label>
            <Textarea rows={5} value={form.contenido_li || ''} onChange={(e) => setForm({ ...form, contenido_li: e.target.value })} />
          </div>
          <div className="border-t pt-3">
            <Label className="font-semibold">Engagement manual</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {['instagram', 'facebook', 'linkedin'].map((plat) => (
                <div key={plat} className="border rounded p-2 bg-slate-50">
                  <p className="text-xs font-semibold uppercase mb-1">{plat}</p>
                  {['likes', 'comments', 'clicks', 'saves', 'reach'].map((k) => (
                    <div key={k} className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-slate-600 w-14">{k}</span>
                      <Input
                        type="number"
                        className="h-7"
                        value={(eng[plat] || {})[k] || 0}
                        onChange={(e) => setEng(plat, k, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => onSave({
              titulo: form.titulo,
              pilar: form.pilar,
              estado: form.estado,
              fecha_programada: form.fecha_programada,
              contenido_ig: form.contenido_ig,
              contenido_fb: form.contenido_fb,
              contenido_li: form.contenido_li,
              engagement: form.engagement
            })}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NewPostModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ titulo: '', pilar: PILARES[0], plataformas: ['Facebook'], contenido_ig: '', contenido_fb: '', contenido_li: '', fecha_programada: '', asset_id: '' });
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    if (open) {
      api.get('/redes/biblioteca').then((r) => setAssets(r.data.assets || [])).catch(() => {});
    }
  }, [open]);

  const togglePlat = (p) => {
    setForm((f) => ({
      ...f,
      plataformas: f.plataformas.includes(p) ? f.plataformas.filter((x) => x !== p) : [...f.plataformas, p]
    }));
  };

  const submit = async () => {
    if (!form.titulo || form.titulo.length < 3) { toast.error('Título mín 3 caracteres'); return; }
    if (form.fecha_programada && new Date(form.fecha_programada) < new Date()) {
      toast.error('La fecha programada debe ser futura');
      return;
    }
    try {
      const payload = { ...form };
      if (payload.fecha_programada) {
        payload.fecha_programada = new Date(payload.fecha_programada).toISOString();
      } else {
        delete payload.fecha_programada;
      }
      if (!payload.asset_id) delete payload.asset_id;
      await api.post('/redes/posts', payload);
      toast.success(form.fecha_programada ? 'Post programado · se publicará automáticamente' : 'Borrador creado');
      onCreated();
      setForm({ titulo: '', pilar: PILARES[0], plataformas: ['Facebook'], contenido_ig: '', contenido_fb: '', contenido_li: '', fecha_programada: '', asset_id: '' });
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo Post</DialogTitle>
          <DialogDescription>Si pones una fecha futura, se publicará automáticamente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Título *</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Título interno del post" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pilar</Label>
              <Select value={form.pilar} onValueChange={(v) => setForm({ ...form, pilar: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PILARES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Programar publicación (opcional)</Label>
              <Input
                type="datetime-local"
                value={form.fecha_programada}
                onChange={(e) => setForm({ ...form, fecha_programada: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Plataformas</Label>
            <div className="flex gap-2 mt-1">
              {['Facebook', 'Instagram', 'LinkedIn'].map((p) => {
                const active = form.plataformas.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlat(p)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300'}`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label>Asset (imagen) — requerida para publicar en Instagram</Label>
            <Select value={form.asset_id || '__none__'} onValueChange={(v) => setForm({ ...form, asset_id: v === '__none__' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Elegir asset..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sin imagen —</SelectItem>
                {assets.filter((a) => a.tipo === 'imagen').map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.filename}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.plataformas.includes('Instagram') && (
            <div>
              <Label>Contenido Instagram</Label>
              <Textarea rows={3} value={form.contenido_ig} onChange={(e) => setForm({ ...form, contenido_ig: e.target.value })} />
            </div>
          )}
          {form.plataformas.includes('Facebook') && (
            <div>
              <Label>Contenido Facebook</Label>
              <Textarea rows={3} value={form.contenido_fb} onChange={(e) => setForm({ ...form, contenido_fb: e.target.value })} />
            </div>
          )}
          {form.plataformas.includes('LinkedIn') && (
            <div>
              <Label>Contenido LinkedIn <span className="text-xs text-slate-500">(no se publica automáticamente)</span></Label>
              <Textarea rows={3} value={form.contenido_li} onChange={(e) => setForm({ ...form, contenido_li: e.target.value })} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700">
            {form.fecha_programada ? 'Programar' : 'Crear borrador'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PublicarModal = ({ open, form, setForm, publishing, result, onClose, onPublicar, assets, onLoadAssets }) => {
  const requiereImagen = form.plataformas.includes('instagram');
  const tienePF = form.plataformas.includes('facebook');
  const tienePI = form.plataformas.includes('instagram');

  useEffect(() => {
    if (open && (!assets || assets.length === 0)) {
      onLoadAssets && onLoadAssets();
    }
  }, [open, assets, onLoadAssets]);

  const toggle = (plat) => {
    setForm({
      ...form,
      plataformas: form.plataformas.includes(plat)
        ? form.plataformas.filter((p) => p !== plat)
        : [...form.plataformas, plat]
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Publicar directamente en Meta
          </DialogTitle>
          <DialogDescription>
            Publica al instante en tu Facebook Page y/o Instagram Business.
          </DialogDescription>
        </DialogHeader>

        {result && (
          <Card className={`p-4 mb-3 ${result.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="publish-result">
            <div className="flex items-center gap-2 mb-2">
              {result.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
              <span className="font-semibold">{result.mensaje}</span>
            </div>
            {Object.entries(result.resultados || {}).map(([plat, res]) => (
              <div key={plat} className="text-sm flex items-center gap-2 mt-1">
                {res.ok ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="capitalize">{plat}:</span>
                    {res.permalink_url ? (
                      <a href={res.permalink_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        Ver publicación ↗
                      </a>
                    ) : <span className="text-slate-600">Publicado (ID {res.post_id})</span>}
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4 text-rose-600" />
                    <span className="capitalize">{plat}:</span>
                    <span className="text-rose-700 text-xs">{res.error}</span>
                  </>
                )}
              </div>
            ))}
          </Card>
        )}

        {!result && (
          <div className="space-y-3">
            <div>
              <Label className="mb-2 block">Plataformas</Label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer p-3 border rounded flex-1 hover:bg-blue-50 transition" data-testid="checkbox-fb">
                  <input
                    type="checkbox"
                    checked={tienePF}
                    onChange={() => toggle('facebook')}
                    className="w-4 h-4"
                  />
                  <Facebook className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium">Facebook Page</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-3 border rounded flex-1 hover:bg-rose-50 transition" data-testid="checkbox-ig">
                  <input
                    type="checkbox"
                    checked={tienePI}
                    onChange={() => toggle('instagram')}
                    className="w-4 h-4"
                  />
                  <Instagram className="w-4 h-4 text-rose-600" />
                  <span className="text-sm font-medium">Instagram</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Texto / Caption *</Label>
              <Textarea
                rows={8}
                value={form.caption}
                onChange={(e) => setForm({ ...form, caption: e.target.value })}
                placeholder="El texto que se publicará..."
                data-testid="input-caption"
              />
              <p className="text-xs text-slate-500 mt-1">
                {form.caption.length} caracteres
                {tienePI && form.caption.length > 2200 && <span className="text-rose-600"> · IG máx 2.200</span>}
              </p>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                Imagen
                {requiereImagen && <Badge variant="outline" className="bg-rose-50 text-rose-700 text-xs">Requerida para Instagram</Badge>}
              </Label>
              <div className="space-y-2 mt-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-32">Desde biblioteca:</span>
                  <Select value={form.asset_id || '__none__'} onValueChange={(v) => setForm({ ...form, asset_id: v === '__none__' ? '' : v, image_url: '' })}>
                    <SelectTrigger data-testid="select-asset">
                      <SelectValue placeholder="Elegir asset..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin imagen —</SelectItem>
                      {(assets || []).filter((a) => a.tipo === 'imagen').map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-32">O URL pública:</span>
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value, asset_id: e.target.value ? '' : form.asset_id })}
                    placeholder="https://..."
                    data-testid="input-image-url"
                  />
                </div>
              </div>
              {requiereImagen && !form.asset_id && !form.image_url && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Instagram requiere una imagen para publicar.
                </p>
              )}
            </div>

            {tienePF && !requiereImagen && (
              <div>
                <Label>Link (opcional, solo Facebook)</Label>
                <Input
                  value={form.link || ''}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {result ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!result && (
            <Button
              onClick={onPublicar}
              disabled={publishing || !form.plataformas.length || !form.caption}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-confirmar-publicar"
            >
              {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Publicar ahora
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const AssetCard = ({ asset, onDelete }) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/redes/biblioteca/${asset.id}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  // Note: backend requires auth, so direct img tag won't pass header. Show metadata + open in new tab via fetch.
  return (
    <Card className="overflow-hidden">
      <div className="aspect-square bg-slate-100 flex items-center justify-center relative">
        {asset.tipo === 'imagen' ? (
          <AuthImage assetId={asset.id} alt={asset.filename} />
        ) : (
          <div className="text-center text-slate-500">
            <ImageIcon className="w-10 h-10 mx-auto opacity-50" />
            <p className="text-xs mt-1">{asset.ext.toUpperCase()}</p>
          </div>
        )}
      </div>
      <div className="p-2">
        <p className="text-xs font-medium truncate">{asset.filename}</p>
        <p className="text-xs text-slate-500">{(asset.size_bytes / 1024).toFixed(0)} KB</p>
        <div className="flex items-center gap-1 mt-2">
          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => downloadAuth(url, token, asset.filename)}>
            Descargar
          </Button>
          <Button size="sm" variant="outline" className="text-rose-600" onClick={onDelete}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

const AuthImage = ({ assetId, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let active = true;
    let objectUrl = null;
    (async () => {
      try {
        const r = await api.get(`/redes/biblioteca/${assetId}`, { responseType: 'blob' });
        if (!active) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      } catch {}
    })();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [assetId]);
  if (!src) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  return <img src={src} alt={alt} className="w-full h-full object-cover" />;
};

const downloadAuth = async (url, token, filename) => {
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(obj), 1000);
  } catch (e) {
    toast.error('Error descargando');
  }
};

const ReporteView = ({ reporte }) => {
  const e = reporte.engagement_total || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Posts publicados" value={reporte.posts_publicados} />
        <KPI label="Likes" value={e.likes} />
        <KPI label="Comentarios" value={e.comments} />
        <KPI label="Clics" value={e.clicks} />
        <KPI label="Reach" value={e.reach} />
      </div>

      <Card className="p-5">
        <h3 className="font-semibold mb-2">Por Pilar</h3>
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs uppercase">
            <tr><th className="text-left py-1">Pilar</th><th>Posts</th><th>Likes</th><th>Coment.</th><th>Clics</th></tr>
          </thead>
          <tbody>
            {Object.entries(reporte.por_pilar || {}).map(([k, v]) => (
              <tr key={k} className="border-t">
                <td className="py-1.5">{k}</td>
                <td className="text-center">{v.posts}</td>
                <td className="text-center">{v.likes}</td>
                <td className="text-center">{v.comments}</td>
                <td className="text-center">{v.clicks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {reporte.mejor_post?.titulo && (
        <Card className="p-5 bg-emerald-50 border-emerald-200">
          <h3 className="font-semibold text-emerald-800">Mejor post de la semana</h3>
          <p className="text-sm mt-1">{reporte.mejor_post.titulo}</p>
          <p className="text-xs text-slate-600 mt-1">Engagement total: {reporte.mejor_post.engagement_total}</p>
        </Card>
      )}

      <Card className="p-5 bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-800 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Sugerencias IA
        </h3>
        {reporte.sugerencias_ia?.lo_que_mas_gusto && (
          <p className="text-sm mt-2"><b>Lo que más gustó:</b> {reporte.sugerencias_ia.lo_que_mas_gusto}</p>
        )}
        {Array.isArray(reporte.sugerencias_ia?.sugerencias_proactivas) && (
          <ul className="list-disc pl-5 mt-2 text-sm space-y-1">
            {reporte.sugerencias_ia.sugerencias_proactivas.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        )}
        {reporte.sugerencias_ia?.optimizacion && (
          <p className="text-sm mt-2"><b>Optimización:</b> {reporte.sugerencias_ia.optimizacion}</p>
        )}
        {reporte.sugerencias_ia?._raw && (
          <pre className="text-xs mt-2 whitespace-pre-wrap text-slate-600">{reporte.sugerencias_ia._raw}</pre>
        )}
      </Card>
    </div>
  );
};

const KPI = ({ label, value }) => (
  <Card className="p-4 text-center">
    <p className="text-xs uppercase text-slate-500">{label}</p>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value ?? 0}</p>
  </Card>
);

const StatsSocialesView = ({ setup, setupLoading, dashboard, topPosts, loading, syncing, onSync, onRefresh }) => {
  const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const isReady = setup?.ok === true;
  const tieneToken = setup?.token_presente;
  const faltanPermisos = (setup?.permisos_faltantes || []).length > 0;
  const sinIG = setup && setup.page && !setup.instagram;

  return (
    <div className="space-y-5" data-testid="stats-view">
      {/* === Diagnóstico === */}
      <Card className={`p-5 border-2 ${isReady ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {isReady ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
            ) : (
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            )}
            <div className="min-w-0">
              <h3 className={`font-semibold ${isReady ? 'text-emerald-900' : 'text-amber-900'}`}>
                {isReady ? 'Conexión Meta lista' : 'Configuración Meta incompleta'}
              </h3>
              {setupLoading && <p className="text-sm text-slate-500">Verificando...</p>}
              {!setupLoading && setup && (
                <p className="text-sm text-slate-700 mt-1">{setup.siguiente_paso}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={setupLoading}>
              {setupLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
              Verificar de nuevo
            </Button>
            <Button
              onClick={onSync}
              disabled={syncing || !isReady}
              data-testid="btn-sync-stats"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
              Sincronizar
            </Button>
          </div>
        </div>

        {/* Detalle del setup */}
        {setup && !setupLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs uppercase text-slate-500">Facebook Page</p>
              {setup.page ? (
                <>
                  <p className="text-sm font-semibold mt-1">{setup.page.name}</p>
                  <p className="text-xs text-slate-500">{setup.page.fans?.toLocaleString('es-CL') || 0} seguidores</p>
                </>
              ) : (
                <p className="text-sm text-rose-600 mt-1">No detectada</p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs uppercase text-slate-500">Instagram Business</p>
              {setup.instagram ? (
                <>
                  <p className="text-sm font-semibold mt-1">@{setup.instagram.username}</p>
                  <p className="text-xs text-slate-500">
                    {setup.instagram.followers_count?.toLocaleString('es-CL') || '—'} seguidores
                  </p>
                </>
              ) : (
                <p className="text-sm text-rose-600 mt-1">No vinculado a la Page</p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs uppercase text-slate-500">Permisos del token</p>
              {faltanPermisos ? (
                <>
                  <p className="text-sm text-rose-700 mt-1">Faltan {setup.permisos_faltantes.length}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-tight">
                    {setup.permisos_faltantes.join(', ')}
                  </p>
                </>
              ) : tieneToken ? (
                <p className="text-sm text-emerald-700 mt-1">✓ Todos OK</p>
              ) : (
                <p className="text-sm text-rose-600 mt-1">Sin token</p>
              )}
            </div>
          </div>
        )}

        {/* Guía de pasos cuando no está listo */}
        {!isReady && setup && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
            <p className="text-sm font-semibold text-slate-900 mb-2">📋 Pasos para activar las estadísticas:</p>
            <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5">
              {sinIG && (
                <li>
                  <strong>Vincula Instagram</strong> a la Facebook Page Peritum: en la app de Instagram → Configuración → Tipo de cuenta → Cambiar a Empresa → Seleccionar Page "Peritum Defensa Corporativa".
                </li>
              )}
              {faltanPermisos && (
                <li>
                  <strong>Regenera el System User Token</strong> en{' '}
                  <a href="https://business.facebook.com/settings" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                    business.facebook.com/settings
                  </a>{' '}
                  → Usuarios del Sistema → Generar nuevo token con permisos:{' '}
                  <code className="text-xs bg-slate-100 px-1 rounded">{setup.permisos_faltantes.join(' ')}</code>
                </li>
              )}
              <li>
                Actualiza <code className="text-xs bg-slate-100 px-1 rounded">META_SYSTEM_USER_TOKEN</code> en backend/.env y reinicia.
              </li>
              <li>
                Vuelve aquí y haz clic en "Verificar de nuevo". Cuando todo aparezca en verde, "Sincronizar".
              </li>
            </ol>
          </div>
        )}

        {setup?.last_sync_at && (
          <p className="text-xs text-slate-500 mt-3">
            Última sincronización: {new Date(setup.last_sync_at).toLocaleString('es-CL')}
            {' · '}{setup.ultima_sync_fb || 0} posts FB · {setup.ultima_sync_ig || 0} posts IG
          </p>
        )}
      </Card>

      {/* === Dashboard (solo si hay data sincronizada) === */}
      {loading && (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
      )}

      {!loading && dashboard && dashboard.total_posts === 0 && (
        <Card className="p-10 text-center text-slate-500" data-testid="stats-empty">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Sin estadísticas todavía</p>
          <p className="text-sm mt-1">Una vez que tu conexión esté lista, haz clic en "Sincronizar" para traer tus publicaciones.</p>
        </Card>
      )}

      {!loading && dashboard && dashboard.total_posts > 0 && (
        <>
          {/* KPIs por plataforma */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5 border-blue-200 bg-blue-50/40">
              <div className="flex items-center gap-2 mb-3">
                <Facebook className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-slate-900">Facebook</h3>
                <span className="text-xs text-slate-500">últimos {dashboard.ventana_dias}d</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <KPI label="Posts" value={dashboard.by_platform.facebook.posts} />
                <KPI label="Alcance" value={dashboard.by_platform.facebook.reach?.toLocaleString('es-CL')} />
                <KPI label="Impresiones" value={dashboard.by_platform.facebook.impressions?.toLocaleString('es-CL')} />
                <KPI label="Engagement" value={dashboard.by_platform.facebook.engagement?.toLocaleString('es-CL')} />
              </div>
            </Card>

            <Card className="p-5 border-rose-200 bg-rose-50/40">
              <div className="flex items-center gap-2 mb-3">
                <Instagram className="w-5 h-5 text-rose-600" />
                <h3 className="font-semibold text-slate-900">Instagram</h3>
                <span className="text-xs text-slate-500">últimos {dashboard.ventana_dias}d</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <KPI label="Posts" value={dashboard.by_platform.instagram.posts} />
                <KPI label="Alcance" value={dashboard.by_platform.instagram.reach?.toLocaleString('es-CL')} />
                <KPI label="Likes" value={dashboard.by_platform.instagram.likes?.toLocaleString('es-CL')} />
                <KPI label="Guardados" value={dashboard.by_platform.instagram.saves?.toLocaleString('es-CL')} />
              </div>
            </Card>
          </div>

          {/* Mejor día / hora */}
          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Mejor día y hora para publicar</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase text-slate-500 mb-2">Engagement por día de la semana</p>
                <div className="flex items-end gap-2 h-32">
                  {(dashboard.engagement_by_day_of_week || []).map((v, i) => {
                    const max = Math.max(...(dashboard.engagement_by_day_of_week || [1]));
                    const h = max > 0 ? Math.max(8, (v / max) * 100) : 8;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="text-xs text-slate-600">{v}</div>
                        <div className="w-full bg-blue-500 rounded-t transition-all" style={{ height: `${h}%`, minHeight: '4px' }} />
                        <div className="text-xs text-slate-500">{DIAS[i]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500 mb-2">Engagement por hora</p>
                <div className="grid grid-cols-12 gap-1">
                  {(dashboard.engagement_by_hour || []).map((v, i) => {
                    const max = Math.max(...(dashboard.engagement_by_hour || [1]));
                    const intensity = max > 0 ? v / max : 0;
                    return (
                      <div
                        key={i}
                        className="aspect-square rounded text-xs flex items-center justify-center font-mono"
                        style={{
                          backgroundColor: `rgba(59, 130, 246, ${0.1 + intensity * 0.8})`,
                          color: intensity > 0.5 ? 'white' : '#475569'
                        }}
                        title={`${i}:00 — ${v} engagement`}
                      >
                        {i}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>

          {/* Top posts */}
          {topPosts.length > 0 && (
            <Card className="p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Top {Math.min(10, topPosts.length)} publicaciones por engagement</h3>
              <div className="space-y-2" data-testid="top-posts">
                {topPosts.map((p) => (
                  <TopPostRow key={`${p.platform}-${p.post_id}`} post={p} />
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

const TopPostRow = ({ post }) => {
  const isIG = post.platform === 'instagram';
  const Icon = isIG ? Instagram : Facebook;
  const date = post.created_time ? new Date(post.created_time).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) : '—';
  return (
    <div className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 transition">
      {post.image_url ? (
        <img src={post.image_url} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon className={`w-6 h-6 ${isIG ? 'text-rose-500' : 'text-blue-600'}`} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <Icon className={`w-3.5 h-3.5 ${isIG ? 'text-rose-500' : 'text-blue-600'}`} />
          <span className="text-xs text-slate-500">{date}</span>
        </div>
        <p className="text-sm text-slate-900 truncate">{post.message || '(sin texto)'}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {post.engagement}</span>
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {post.reach}</span>
        </div>
      </div>
      {post.permalink_url && (
        <a href={post.permalink_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 flex-shrink-0">
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );
};

export default RedesSociales;
